import type { Centre, TelegramWidgetData } from '@ielts-buddy/shared'
import { create } from 'zustand'
import {
  ApiError,
  centreLogin,
  clearToken,
  fetchCentreMe,
  getToken,
  setToken,
} from './api.js'

type Status = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error'

interface AuthState {
  status: Status
  centre: Centre | null
  error: string | null
  widgetResetNonce: number
  bootstrap(): Promise<void>
  login(data: TelegramWidgetData): Promise<void>
  logout(): void
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'idle',
  centre: null,
  error: null,
  widgetResetNonce: 0,

  async bootstrap() {
    set({ status: 'loading', error: null })

    const token = getToken()
    if (token) {
      try {
        const { centre } = await fetchCentreMe()
        set({ status: 'authenticated', centre, error: null })
        return
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) clearToken()
      }
    }

    set({ status: 'unauthenticated' })
  },

  async login(data: TelegramWidgetData) {
    set({ status: 'loading', error: null })
    try {
      const { centre, token } = await centreLogin(data)
      setToken(token)
      set({ status: 'authenticated', centre, error: null })
    } catch (err) {
      clearToken()
      set({ status: 'error', centre: null, error: (err as Error).message })
    }
  },

  logout() {
    clearToken()
    set((state) => ({
      status: 'unauthenticated',
      centre: null,
      error: null,
      widgetResetNonce: state.widgetResetNonce + 1,
    }))
  },
}))
